/**
 * Page context — makes the agent navigation-aware.
 * ---------------------------------------------------------------------------
 * Tracks which page (and, on the landing page, which section) the visitor is
 * currently looking at. Every outgoing chat message gets a machine-readable
 * suffix appended by messagingService#sendTextMessage:
 *
 *     [[page:units]]   /   [[page:home/residences]]   /   [[page:neighborhood]]
 *
 * The agent's instructions treat the tag as invisible context ("the visitor is
 * currently viewing …") — it must never be echoed or mentioned. The client
 * strips the tag before rendering the visitor's own messages, so it is only
 * visible in the raw Salesforce transcript (useful for debugging).
 *
 * The page part is set by App.js on route changes; the section part by the
 * landing page's IntersectionObserver as the visitor scrolls.
 */

const PAGE_TAG_PATTERN = /\s*\[\[page:[^\]]*\]\]\s*$/;

let currentPage = "home";
let currentSection = "";

/** App.js route sync: 'home' | 'units' | 'neighborhood'. */
export function setCurrentPage(page) {
    currentPage = page || "home";
    if (currentPage !== "home") {
        currentSection = "";
    }
}

/** Landing-page section sync (e.g. 'residences', 'neighborhood'). */
export function setCurrentSection(section) {
    currentSection = section || "";
}

/** Current context id, e.g. 'home', 'home/residences', 'units'. */
export function getPageContext() {
    return currentSection ? `${currentPage}/${currentSection}` : currentPage;
}

/** Append the page tag to an outgoing message. */
export function withPageTag(text) {
    if (typeof text !== "string" || !text.trim()) {
        return text;
    }
    return `${text} [[page:${getPageContext()}]]`;
}

/** Strip the page tag from a message for display. */
export function stripPageTag(text) {
    if (typeof text !== "string") {
        return text;
    }
    return text.replace(PAGE_TAG_PATTERN, "");
}

// Debug/console handle, mirroring window.awacPoiMapBridge.
if (typeof window !== "undefined") {
    window.awacPageContext = { getPageContext };
}
